import { useState } from "react";
import { EVENT_TYPES, typeOf, SERVICE_LABELS } from "@/lib/eventTypes";
import { euros } from "@/lib/weddingApi";
import { Info, AlertTriangle } from "lucide-react";

const toCents = (v) => Math.round(parseFloat(String(v).replace(",", ".") || 0) * 100);

export const emptyReception = (defaults) => ({
  event_type: "",
  event_type_other: "",
  event_title: "",
  detail_value: "",
  organizer_name: "",
  contact_email: "",
  contact_phone: "",
  event_date: "",
  arrival_time: "",
  start_time: "",
  end_time: "",
  adults_count: "",
  children_count: "",
  cocktail_participants: "",
  cheese_participants: "",
  adult_price: ((defaults?.adult_price_cents ?? 10500) / 100).toFixed(2),
  child_price: ((defaults?.child_price_cents ?? 3500) / 100).toFixed(2),
  child_max_age: defaults?.child_max_age ?? 12,
  cocktail_price: ((defaults?.cocktail_price_cents ?? 3500) / 100).toFixed(2),
  cheese_price: ((defaults?.cheese_price_cents ?? 1000) / 100).toFixed(2),
  deposit_percent: defaults?.deposit_percent ?? 50,
  adult_services: ["starter", "main", "dessert"],
  child_services: ["main", "dessert"],
  max_proposals_per_service: 2,
  deadline_mode: "relative",
  deadline_days_before: 10,
  finalization_deadline: "",
  deadline_reason: "",
  acceptance_proof: "",
  acceptance_confirmed: false,
  internal_notes: "",
  is_demo: false,
});

export function buildPayload(f) {
  const type = typeOf(f.event_type);
  const details = {};
  if (type?.detail && f.detail_value) details[type.detail.key] = f.detail_value;
  return {
    event_type: f.event_type,
    event_type_other: f.event_type_other || "",
    event_title: f.event_title.trim(),
    organizer_name: f.organizer_name || "",
    event_details: details,
    contact_email: f.contact_email,
    contact_phone: f.contact_phone || "",
    event_date: f.event_date,
    arrival_time: f.arrival_time || "",
    start_time: f.start_time || "",
    end_time: f.end_time || "",
    acceptance_proof: f.acceptance_proof || "",
    acceptance_confirmed: !!f.acceptance_confirmed,
    internal_notes: f.internal_notes || "",
    is_demo: !!f.is_demo,
    service_config: {
      adult_services: f.adult_services,
      child_services: f.child_services,
      max_proposals_per_service: Number(f.max_proposals_per_service) || 2,
      collective_mode: false,
    },
    deadline: {
      mode: f.deadline_mode,
      days_before: Number(f.deadline_days_before) || 0,
      finalization_deadline: f.deadline_mode === "manual" ? f.finalization_deadline : "",
      reason: f.deadline_reason || "",
    },
    contract: {
      adult_price_cents: toCents(f.adult_price),
      child_price_cents: toCents(f.child_price),
      child_max_age: Number(f.child_max_age) || 12,
      cocktail_price_cents: toCents(f.cocktail_price),
      cheese_price_cents: toCents(f.cheese_price),
      deposit_percent: Number(f.deposit_percent) || 0,
      adults_count: Number(f.adults_count) || 0,
      children_count: Number(f.children_count) || 0,
      cocktail_participants: Number(f.cocktail_participants) || 0,
      cheese_participants: Number(f.cheese_participants) || 0,
    },
  };
}

function Section({ title, hint, children }) {
  return (
    <section className="mt-8 first:mt-0">
      <h3 className="overline text-[#B8956A]">{title}</h3>
      {hint && <p className="mt-2 text-xs text-[#697585]">{hint}</p>}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

export function LabeledField({ label, hint, required, children, wide }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.14em] text-[#203240]">
        {label}{required && <span className="text-[#B8956A]"> *</span>}
      </span>
      <div className="mt-2">{children}</div>
      {hint && <span className="mt-1.5 block text-xs text-[#697585]">{hint}</span>}
    </label>
  );
}

/** Full, explicitly labelled creation form. Everything stays editable afterwards. */
export default function ReceptionForm({ form, setForm, onSubmit, onCancel, submitting, error }) {
  const [touched, setTouched] = useState(false);
  const type = typeOf(form.event_type);
  const set = (patch) => setForm({ ...form, ...patch });

  const daysUntil = form.event_date
    ? Math.ceil((new Date(`${form.event_date}T12:00:00`) - new Date()) / 86400000)
    : null;
  const shortNotice = daysUntil !== null && daysUntil <= Number(form.deadline_days_before || 10);

  const missing = [];
  if (!form.event_type) missing.push("le type d'événement");
  if (form.event_type === "other" && !form.event_type_other.trim()) missing.push("la précision du type");
  if (form.event_title.trim().length < 2) missing.push("le titre de la réception");
  if (!form.contact_email.trim()) missing.push("l'email de l'organisateur");
  if (!form.event_date) missing.push("la date");

  const toggle = (key, value) => {
    const list = form[key] || [];
    set({ [key]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] });
  };

  return (
    <div data-testid="reception-form" className="bg-white border border-[#D9D3C5] w-full max-w-3xl p-7 md:p-9 max-h-[92vh] overflow-auto">
      <h2 className="font-display text-3xl font-light">Nouveau dossier de réception</h2>
      <p className="mt-2 text-sm text-[#697585]">
        Tous les champs sont modifiables ensuite dans la fiche du dossier. Rien n'est envoyé au client
        à la création.
      </p>

      <Section title="1 · Type et intitulé" hint="Le type pilote les textes de l'espace client et les filtres, jamais les tarifs.">
        <LabeledField label="Type d'événement" required>
          <select data-testid="form-event-type" className="field w-full" value={form.event_type}
                  onChange={(e) => set({ event_type: e.target.value })}>
            <option value="">— Choisir un type —</option>
            {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </LabeledField>
        {form.event_type === "other" && (
          <LabeledField label="Précisez le type" required hint="Exemple : départ en retraite, remise de diplôme.">
            <input data-testid="form-event-type-other" className="field w-full" value={form.event_type_other}
                   onChange={(e) => set({ event_type_other: e.target.value })} />
          </LabeledField>
        )}
        <LabeledField label="Titre de la réception" required wide
                      hint={type ? `Exemple : ${type.example}` : "Choisissez d'abord un type pour voir un exemple."}>
          <input data-testid="form-event-title" className="field w-full" value={form.event_title}
                 placeholder={type?.example || "Titre affiché dans l'espace client"}
                 onChange={(e) => set({ event_title: e.target.value })} />
        </LabeledField>
        {type?.detail && (
          <LabeledField label={type.detail.label} hint="Facultatif — jamais demandé pour une réception après obsèques.">
            <input data-testid="form-detail-value" className="field w-full" value={form.detail_value}
                   onChange={(e) => set({ detail_value: e.target.value })} />
          </LabeledField>
        )}
      </Section>

      <Section title="2 · Organisateur et contact" hint="L'organisateur est la personne qui recevra l'accès, pas forcément la personne célébrée.">
        <LabeledField label="Nom de l'organisateur" hint="Requis avant l'envoi de l'accès.">
          <input data-testid="form-organizer" className="field w-full" value={form.organizer_name}
                 onChange={(e) => set({ organizer_name: e.target.value })} />
        </LabeledField>
        <LabeledField label="Email de l'organisateur" required>
          <input data-testid="form-email" className="field w-full" type="email" value={form.contact_email}
                 onChange={(e) => set({ contact_email: e.target.value })} />
        </LabeledField>
        <LabeledField label="Téléphone">
          <input data-testid="form-phone" className="field w-full" value={form.contact_phone}
                 onChange={(e) => set({ contact_phone: e.target.value })} />
        </LabeledField>
      </Section>

      <Section title="3 · Date et horaires">
        <LabeledField label="Date de la réception" required>
          <input data-testid="form-date" type="date" className="field w-full" value={form.event_date}
                 onChange={(e) => set({ event_date: e.target.value })} />
        </LabeledField>
        <LabeledField label="Arrivée des invités" hint="Format 18:30">
          <input data-testid="form-arrival" className="field w-full" placeholder="18:30" value={form.arrival_time}
                 onChange={(e) => set({ arrival_time: e.target.value })} />
        </LabeledField>
        <LabeledField label="Début du service" hint="Format 19:00">
          <input data-testid="form-start" className="field w-full" placeholder="19:00" value={form.start_time}
                 onChange={(e) => set({ start_time: e.target.value })} />
        </LabeledField>
        <LabeledField label="Fin prévue" hint="Format 02:00">
          <input data-testid="form-end" className="field w-full" placeholder="02:00" value={form.end_time}
                 onChange={(e) => set({ end_time: e.target.value })} />
        </LabeledField>
      </Section>

      <Section title="4 · Effectifs prévus au contrat" hint="Les effectifs réels seront renseignés par l'organisateur dans son espace.">
        <LabeledField label="Adultes prévus">
          <input data-testid="form-adults" type="number" min="0" className="field w-full" value={form.adults_count}
                 onChange={(e) => set({ adults_count: e.target.value })} />
        </LabeledField>
        <LabeledField label={`Enfants prévus (jusqu'à ${form.child_max_age} ans)`}>
          <input data-testid="form-children" type="number" min="0" className="field w-full" value={form.children_count}
                 onChange={(e) => set({ children_count: e.target.value })} />
        </LabeledField>
        <LabeledField label="Participants au cocktail" hint="0 si aucun cocktail prévu.">
          <input data-testid="form-cocktail-participants" type="number" min="0" className="field w-full"
                 value={form.cocktail_participants} onChange={(e) => set({ cocktail_participants: e.target.value })} />
        </LabeledField>
        <LabeledField label="Participants à l'assiette de fromages" hint="0 si non prévue.">
          <input data-testid="form-cheese-participants" type="number" min="0" className="field w-full"
                 value={form.cheese_participants} onChange={(e) => set({ cheese_participants: e.target.value })} />
        </LabeledField>
      </Section>

      <Section title="5 · Tarifs convenus" hint="Préremplis avec votre modèle de référence — remplacez-les par les prix de la proposition acceptée si besoin.">
        <LabeledField label="Prix par adulte (€)">
          <input data-testid="form-adult-price" className="field w-full" value={form.adult_price}
                 onChange={(e) => set({ adult_price: e.target.value })} />
        </LabeledField>
        <LabeledField label="Prix par enfant (€)">
          <input data-testid="form-child-price" className="field w-full" value={form.child_price}
                 onChange={(e) => set({ child_price: e.target.value })} />
        </LabeledField>
        <LabeledField label="Âge maximum « enfant »">
          <input className="field w-full" value={form.child_max_age}
                 onChange={(e) => set({ child_max_age: e.target.value })} />
        </LabeledField>
        <LabeledField label="Cocktail par personne (€)">
          <input className="field w-full" value={form.cocktail_price}
                 onChange={(e) => set({ cocktail_price: e.target.value })} />
        </LabeledField>
        <LabeledField label="Assiette de fromages par personne (€)">
          <input className="field w-full" value={form.cheese_price}
                 onChange={(e) => set({ cheese_price: e.target.value })} />
        </LabeledField>
        <LabeledField label="Acompte à la signature (%)">
          <input className="field w-full" value={form.deposit_percent}
                 onChange={(e) => set({ deposit_percent: e.target.value })} />
        </LabeledField>
        <p className="sm:col-span-2 flex gap-2 text-xs text-[#697585]">
          <Info size={14} className="shrink-0 mt-0.5 text-[#B8956A]" />
          Privatisation incluse dans le prix des menus — aucun frais de location supplémentaire
          ({euros(0)}). Le dépassement horaire se règle dans la fiche du dossier.
        </p>
      </Section>

      <Section title="6 · Services de restauration" hint="Un baptême et un mariage peuvent partager la même formule. La complétude des invités dépend de ces services.">
        <LabeledField label="Services pour les adultes" wide>
          <div className="flex flex-wrap gap-4">
            {Object.entries(SERVICE_LABELS).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid={`form-adult-service-${k}`}
                       checked={form.adult_services.includes(k)} onChange={() => toggle("adult_services", k)} />
                {label}
              </label>
            ))}
          </div>
        </LabeledField>
        <LabeledField label="Services pour les enfants" wide hint="L'entrée n'est jamais obligatoire pour un enfant.">
          <div className="flex flex-wrap gap-4">
            {Object.entries(SERVICE_LABELS).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid={`form-child-service-${k}`}
                       checked={form.child_services.includes(k)} onChange={() => toggle("child_services", k)} />
                {label}
              </label>
            ))}
          </div>
        </LabeledField>
        <LabeledField label="Propositions maximum par service" hint="2 par défaut : les invités choisissent parmi celles-ci.">
          <input data-testid="form-max-proposals" type="number" min="1" max="4" className="field w-full"
                 value={form.max_proposals_per_service}
                 onChange={(e) => set({ max_proposals_per_service: e.target.value })} />
        </LabeledField>
      </Section>

      <Section title="7 · Date limite de finalisation" hint="Remplace le J-10 systématique : vous fixez le délai réellement convenu.">
        <LabeledField label="Mode de calcul">
          <select data-testid="form-deadline-mode" className="field w-full" value={form.deadline_mode}
                  onChange={(e) => set({ deadline_mode: e.target.value })}>
            <option value="relative">Automatique, X jours avant la réception</option>
            <option value="manual">Date fixée à la main</option>
          </select>
        </LabeledField>
        {form.deadline_mode === "relative" ? (
          <LabeledField label="Nombre de jours avant la réception">
            <input data-testid="form-deadline-days" type="number" min="0" className="field w-full"
                   value={form.deadline_days_before}
                   onChange={(e) => set({ deadline_days_before: e.target.value })} />
          </LabeledField>
        ) : (
          <LabeledField label="Date limite" hint="Jusqu'au jour de la réception, avant le service.">
            <input data-testid="form-deadline-date" type="date" className="field w-full"
                   value={form.finalization_deadline}
                   onChange={(e) => set({ finalization_deadline: e.target.value })} />
          </LabeledField>
        )}
        <LabeledField label="Motif (facultatif)" wide hint="Par exemple : réception organisée à court délai.">
          <input data-testid="form-deadline-reason" className="field w-full" value={form.deadline_reason}
                 onChange={(e) => set({ deadline_reason: e.target.value })} />
        </LabeledField>
        {shortNotice && form.deadline_mode === "relative" && (
          <p data-testid="form-short-notice" className="sm:col-span-2 flex gap-2 text-sm text-[#B8956A]">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            Réception à court délai : précisez la date limite convenue (passez en « date fixée à la main »).
            Aucune échéance passée ne sera enregistrée automatiquement.
          </p>
        )}
      </Section>

      <Section title="8 · Acceptation et statut" hint="L'acceptation est indépendante de l'acompte, qui se saisit dans la fiche.">
        <LabeledField label="Preuve d'acceptation (extrait de l'échange)" wide>
          <textarea data-testid="form-acceptance-proof" className="field w-full min-h-[80px]"
                    value={form.acceptance_proof} onChange={(e) => set({ acceptance_proof: e.target.value })} />
        </LabeledField>
        <label className="sm:col-span-2 flex items-start gap-2 text-sm">
          <input type="checkbox" data-testid="form-acceptance-confirmed" className="mt-1"
                 checked={form.acceptance_confirmed}
                 onChange={(e) => set({ acceptance_confirmed: e.target.checked })} />
          Je confirme que cette proposition a été acceptée par le client (nécessaire pour ouvrir un accès).
        </label>
        <LabeledField label="Notes internes" wide hint="Jamais visibles par le client.">
          <textarea data-testid="form-internal-notes" className="field w-full min-h-[70px]"
                    value={form.internal_notes} onChange={(e) => set({ internal_notes: e.target.value })} />
        </LabeledField>
        <label className="sm:col-span-2 flex items-start gap-2 text-sm text-[#697585]">
          <input type="checkbox" data-testid="form-demo" className="mt-1" checked={form.is_demo}
                 onChange={(e) => set({ is_demo: e.target.checked })} />
          Dossier de démonstration (aucun invité réel, accès possible sans acceptation, aucun email envoyé).
        </label>
      </Section>

      {error && <p data-testid="form-error" className="mt-6 text-sm text-red-600">{error}</p>}
      {touched && !!missing.length && (
        <p data-testid="form-missing" className="mt-6 text-sm text-[#B8956A]">
          Il manque {missing.join(", ")}.
        </p>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          data-testid="form-submit"
          className="btn-primary"
          disabled={submitting}
          onClick={() => { setTouched(true); if (!missing.length) onSubmit(); }}
        >
          {submitting ? "Création…" : "Créer le dossier"}
        </button>
        <button className="btn-ghost" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}
